import React from 'react';
import { observer } from 'mobx-react';

// Config
import { config } from '../../config';

// Translations
import translate from '../Translate.js';

// Components
import * as icons from '../Icons/Icons';
import { FeatureFlag } from 'react-launch-darkly';

const AutomatedSettings = observer(class AutomatedSettings extends React.Component {
  constructor() {
    super();

    // Bind this to functions
    this.selectDefault = this.selectDefault.bind(this);
    this.selectPreset = this.selectPreset.bind(this);
    this.renderOption = this.renderOption.bind(this);

    // Set the initial state
    this.state = {
      options: [],
      preset: ''
    }
  }

  componentDidMount() {
    const { ExamStore } = this.props.store;
    const { categorySid, exam } = this.props;

    let { configuration } = ExamStore;

    if(!configuration) {
      configuration = exam.configuration;
    }

    // If this is a new exam, default to strict
    if(exam.sid) {
      ExamStore.getModalityPresets();
    } else {
      ExamStore.getModalityPresets(this.selectDefault);
    }

    if(categorySid) {
      ExamStore.getAutomatedOptions(categorySid);
    }

    // Pre-check any selected modality options
    if(
      configuration &&
      configuration.modalityOptions &&
      Object.keys(configuration.modalityOptions).length
    ) {
      const options = [];

      // Loops through all proctorTypes selected and applies the modality options to each proctor type
      Object.keys(configuration.modalityOptions).forEach(proctorTypeSid => {
        Object.keys(configuration.modalityOptions[proctorTypeSid]).forEach(option => {
          if(proctorTypeSid === categorySid) {
            options.push(option);
          }
        });
      });

      this.setState({
        options
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { handleInput, categorySid } = this.props;
    const { options } = this.state;
    if(this.state.options !== prevState.options) {
      const modalityOptions = {
        recordScreen: {},
        recordWebcam: {},
        recordAudio: {}
      };

      options.forEach(option => {
        modalityOptions[option] = {}
      });

      handleInput('modalityOptions', null, true, null, null, false, modalityOptions, null, categorySid);
    }
  }

  renderOptionIcon(id) {
    let icon = null;

    switch(id) {
      case 'recordWebcam':
        icon = <icons.iconVideoCamera />;
        break;

      case 'recordScreen':
        icon = <icons.iconDesktopRecord />;
        break;

      case 'recordAudio':
        icon = <icons.iconMicrophone />;
        break;

      case 'roomScan':
        icon = <icons.iconVideoCamera360 />;
        break;

      case 'forceFullScreen':
        icon = <icons.iconDesktopFullscreen />;
        break;

      case 'onlyOneScreen':
        icon = <icons.iconDesktopOne />;
        break;

      case 'closeOpenTabs':
        icon = <icons.iconTabsClose />;
        break;

      case 'disableNewTabs':
        icon = <icons.iconTabsOne />;
        break;

      case 'disablePrinting':
        icon = <icons.iconPrintDisable />;
        break;

      case 'disableClipboard':
        icon = <icons.iconClipboardDisable />;
        break;

      case 'disableRightClick':
        icon = <icons.iconRightClickDisable />;
        break;

      case 'clearCache':
        icon = <icons.iconEraser />;
        break;

      case 'verifyId':
        icon = <icons.iconID />;
        break;

      case 'blockedExtensions':
        icon = <icons.iconExtensionBlock />;
        break;

      case 'desktopAppCheck':
        icon = <icons.iconDesktopAppCheck />;
        break;

      default:
        break;
    }

    return icon;
  }

  selectDefault() {
    this.setState({
      preset: config.defaultAutomatedPreset
    }, () => {
      this.selectPreset();
    });
  }

  selectPreset() {
    const { ExamStore } = this.props.store;
    const presetSid = this.preset.value;
    const options = [];

    // Get the preset
    const callback = () => {
      if(ExamStore.modalityPreset && ExamStore.modalityPreset.optionGroups) {
        ExamStore.modalityPreset.optionGroups.forEach(optionGroup => {
          if(optionGroup.options) {
            optionGroup.options.forEach(option => {
              options.push(option.id);
            });
          }

          this.setState({
            options,
            preset: presetSid
          });
        })
      }
    }

    ExamStore.getModalityPreset(presetSid, callback);
  }

  toggleOption(option) {
    const options = [...this.state.options];
    const optionIndex = options.indexOf(option.id);

    if(optionIndex === -1) {
      // Add the option if it’s not selected
      options.push(option.id);
    } else {
      // Otherwise, remove it
      options.splice(optionIndex, 1);
    }

    this.setState({
      options,
      preset: ''
    });
  }

  // Most of our feature flags use booleans but onboarding version
  // returns a string of 'new' or 'old'. Because of this we have to render this
  // feature flag slightly different than the others
  shouldRenderOption(version, option) {
    if (version === 'old') {
      return this.renderOption(option);
    } else {
      return null;
    }
  }

  renderOptionGroup(optionGroup) {
    return optionGroup.options.map((option) => {
      if(option.id === 'desktopAppCheck') {
        return (
          <FeatureFlag
            flagKey="smarter-services-utility-app"
            renderFeatureCallback={() => this.renderOption(option)}
          />
        )
      } else if (option.id === 'closeOpenTabs') {
        return (
          <FeatureFlag
            flagKey="onboarding-version"
            renderFeatureCallback={(version) => this.shouldRenderOption(version, option)}
          />
        )
      } else {
        return this.renderOption(option);
      }
    })
  }

  renderOption(option) {
    const { translation } = this.props
    const { options } = this.state

    return (
      <li
        key={option.id}
        className={`grid__cell block block--selectable${
          options.indexOf(option.id) > -1 ? ' selected' : ''
        }`}
      >
        {this.renderOptionIcon(option.id)}
        {option.title}

        {options.indexOf(option.id) > -1 &&
          <i className="block__check">
            <icons.iconCheck />
            <span className="meta">selected</span>
          </i>
        }

        <button
          className="grid__select"
          type="button"
          onClick={() => this.toggleOption(option)}
        >
          {options.indexOf(option.id) > -1 &&
            translation.options.deselect.replace('%option%', option.title)
          }

          {options.indexOf(option.id) === -1 &&
            translation.options.select.replace('%option%', option.title)
          }
        </button>
        <span className="grid__select-focus"></span>
      </li>
    )
  }

  render() {
    const { categorySid, translation } = this.props;
    const { ExamStore } = this.props.store;
    const { automatedOptions, modalityPresets } = ExamStore;

    return (
      <div className="switch-list__settings text-block">
        <input
          className="inp-hidden"
          type="text"
          value={this.state.options.toString()}
          onChange={null}
          required
          aria-required="true"
          ref={(input) => this.emptyGuard = input}
        />

        {this.state.options.length === 0 &&
          <span className="note note--prominent note--error">
            {translation.error_msg}
          </span>
        }

        <span className="meta">
          {translation.heading}
        </span>

        <div className="form-inline form-inline--alt">
          <label htmlFor={`${categorySid}-preset`}>
            {translation.presets.label}
          </label>

          <label className="select form-inline__main">
            <select
              id={`${categorySid}-preset`}
              onChange={this.selectPreset}
              ref={(input) => this.preset = input}
              value={this.state.preset}
            >
              <option value="">{translation.presets.custom}</option>

              {modalityPresets.map(
                (preset) =>
                <option key={preset.sid} value={preset.sid}>
                  {preset.title}
                </option>
              )}
            </select>
          </label>
        </div>

        {automatedOptions.map(
          (optionGroup) =>
            optionGroup.title !== 'Recording Options' &&
              <section key={optionGroup.sid}>
                <strong className="grid-separator">
                  {optionGroup.title}
                </strong>

                <ul className="grid grid__sm">
                  {this.renderOptionGroup(optionGroup)}
                </ul>
              </section>
        )}
      </div>
    )
  }
})

export default translate('AutomatedSettings')(AutomatedSettings);



// WEBPACK FOOTER //
// ./src/app/components/ExamEdit/AutomatedSettings.js